import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isEmpty, isEqual } from 'lodash';

import Page from 'components/page/Page';
import InfiniteScrollIndicator from 'components/infinite-load-indicator/InfiniteScrollIndicator';

import DiscoveryContentSearch from '../components/search/DiscoveryContentSearch';
import DiscoveryContentInteraction from '../components/interaction/DiscoveryContentInteraction';
import DiscoveryContentResults from '../components/results/DiscoveryContentResults';
import DiscoveryContentFilters from '../components/filters/DiscoveryContentFilters';

import {
  getContent,
  resetContent,
  resetFilters,
  updateContentFilters,
  updateScreenState
} from '../reducers/content';

import { trackEventDiscoveryContentFilters } from 'modules/tracking/utils/tracking';

import { __t, applyTranslations } from 'utils/language';
import { getDiscoverySubnavigation } from 'utils/navigation';
import { getBrandProfileId } from 'utils/organization';
import { getPredefinedHashtag } from '../utils/content';
import { prepareFiltersForAnalytics } from '../utils/tracking';

import './discovery-content-page.scss';

import type {
  AndOrOptions,
  DiscoveryContentFilters as DiscoveryContentFiltersType,
  DiscoveryContentLoading,
  DiscoveryContentRelatedInfo,
  DiscoveryContentResult,
  DiscoveryFilterOptions,
  DiscoveryScreenState,
  Organization
} from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

type StateProps = {
  org: Organization,
  results: Array<DiscoveryContentResult>,
  numContentCreators: number,
  searchTerms: Array<string>,
  searchTermsLogicalOperator: AndOrOptions,
  screenState: DiscoveryScreenState,
  isLoading: DiscoveryContentLoading,
  userChannelConsents: ConsentReducerConsentsMap
};

type DispatchProps = {
  dispatchFetchContent: (options?: DiscoveryFilterOptions) => Promise<void>,
  dispatchUpdateFilters: (a: Partial<DiscoveryContentFiltersType>) => Promise<void>,
  dispatchUpdateScreenState: (a: DiscoveryScreenState) => Promise<void>,
  dispatchResetContent: () => void,
  dispatchResetFilters: (a: boolean) => void
};

type Props = StateProps & DispatchProps;

type State = {
  isFullScreen: boolean
};

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./discovery-content-page.i18n.json'));

const msgKey = 'discovery_content_page';

class DiscoveryContentPage extends React.Component<Props, State> {
  state = {
    isFullScreen: false
  };

  componentDidMount() {
    const { dispatchUpdateFilters } = this.props;

    dispatchUpdateFilters({ searchTerms: getPredefinedHashtag() })
      .then(() => this.handleFetchContent());
  }

  componentWillUnmount() {
    const { dispatchResetContent, dispatchResetFilters } = this.props;

    dispatchResetFilters(true);
    dispatchResetContent();
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const {
      org,
      results,
      isLoading,
      numContentCreators,
      searchTerms,
      searchTermsLogicalOperator,
      screenState
    } = this.props;

    const { isFullScreen } = this.state;

    return !isEqual(org, nextProps.org)
      || !isEqual(screenState, nextProps.screenState)
      || !isEqual(isLoading, nextProps.isLoading)
      || results.length !== nextProps.results.length
      || numContentCreators !== nextProps.numContentCreators
      || searchTerms.length !== nextProps.searchTerms.length
      || searchTermsLogicalOperator !== nextProps.searchTermsLogicalOperator
      || isFullScreen !== nextState.isFullScreen;
  }

  getRelatedInfo(): DiscoveryContentRelatedInfo {
    const { screenState } = this.props;
    const meta = screenState.meta || {};

    return {
      currentPage: screenState.page || 0,
      pagesTotal: meta.pagesTotal || 0,
      itemsTotal: meta.itemsTotal || 0,
      sort: screenState.sort
    };
  }

  canLoadMore() {
    const relatedInfo = this.getRelatedInfo();

    return relatedInfo.currentPage >= 1 && relatedInfo.currentPage < relatedInfo.pagesTotal;
  }

  handleFetchContent = (options?: DiscoveryFilterOptions): void => {
    const { dispatchFetchContent } = this.props;

    dispatchFetchContent(options);
  };

  handleLoadMore = () => {
    const relatedInfo = this.getRelatedInfo();

    this.handleFetchContent({
      page: relatedInfo.currentPage + 1
    });
  };

  handlePageScroll(): void {
    window.scrollTo({ top: 0 });
  }

  handleResetFilters = (): void => {
    this.handlePageScroll();
    this.handleFetchContent();
  };

  handleToggleFilters = (): void => {
    this.handlePageScroll();

    this.setState(state => ({
      isFullScreen: !state.isFullScreen
    }));
  };

  handleUpdateSearchTerms = (searchTerms: Array<string>): void => {
    const { dispatchUpdateFilters } = this.props;

    dispatchUpdateFilters({
      searchTerms
    }).then(() => this.handleFetchContent());
  };

  handleUpdateSearchTermsLogical = (option: AndOrOptions): void => {
    const { org, dispatchUpdateFilters } = this.props;
    const brandProfileId = getBrandProfileId(org);

    const updatedFilters = {
      searchTermsLogicalOperator: option
    };

    const preparedAnalyticsFilters = prepareFiltersForAnalytics(updatedFilters);

    dispatchUpdateFilters(updatedFilters)
      .then(() => this.handleFetchContent());

    if (brandProfileId) {
      trackEventDiscoveryContentFilters(org.id, brandProfileId, preparedAnalyticsFilters);
    }
  };

  handleSort = (sort: string): void => {
    const { screenState, dispatchUpdateScreenState } = this.props;

    dispatchUpdateScreenState({
      ...screenState,
      page: 0,
      sort
    }).then(() => this.handleFetchContent());
  };

  handleLastPageOfGalleryModal = () => {
    const { isLoading } = this.props;
    const resultsLoading = isLoading.results;

    if (!resultsLoading.standard && !resultsLoading.more && this.canLoadMore()) {
      this.handleLoadMore();
    }
  };

  renderInteraction(): React.ReactNode {
    const { numContentCreators } = this.props;
    const { isFullScreen } = this.state;
    const relatedInfo = this.getRelatedInfo();

    return (
      <div className="discovery-content-page__interaction">
        <DiscoveryContentInteraction
          numCreators={numContentCreators}
          numPosts={relatedInfo.itemsTotal}
          sort={relatedInfo.sort}
          isFullScreen={isFullScreen}
          onSort={this.handleSort}
          onToggleFilters={this.handleToggleFilters}
        />
      </div>
    );
  }

  renderSearch(): React.ReactNode {
    const { searchTerms, searchTermsLogicalOperator } = this.props;
    const { isFullScreen } = this.state;

    return (
      <div className="discovery-content-page__search">
        <DiscoveryContentSearch
          key={`discovery-content-page-search--${searchTermsLogicalOperator}`}
          isFullScreen={isFullScreen}
          searchTerms={searchTerms}
          searchTermsLogicalOperator={searchTermsLogicalOperator}
          onSearch={this.handleFetchContent}
          onUpdateSearchTerms={this.handleUpdateSearchTerms}
          onUpdateSearchTermsLogical={this.handleUpdateSearchTermsLogical}
        />
      </div>
    );
  }

  renderInfiniteScrollIndication(): React.ReactNode {
    const { isLoading } = this.props;
    const resultsLoading = isLoading.results;

    return (
      <InfiniteScrollIndicator
        detectionOffset={700}
        isLoading={!resultsLoading.standard && resultsLoading.more}
        onLoadMore={this.handleLoadMore}
      />
    );
  }

  renderResults(): React.ReactNode {
    const { results, isLoading, userChannelConsents, searchTerms } = this.props;
    const { isFullScreen } = this.state;
    const oddTailLength = results.length % 5; // sliced to fit in 5 columns grid
    const visibleItems = (this.canLoadMore() && oddTailLength > 0)
      ? results.slice(0, -oddTailLength)
      : results;

    return (
      <div className="discovery-content-page__results">
        <DiscoveryContentResults
          userChannelConsents={userChannelConsents}
          results={visibleItems}
          isFullScreen={isFullScreen}
          isLoading={isLoading}
          searchTerms={searchTerms}
          onGalleryModalLastPage={this.handleLastPageOfGalleryModal}
        />
        {
          !isEmpty(visibleItems) && this.canLoadMore() &&
            this.renderInfiniteScrollIndication()
        }
      </div>
    );
  }

  renderSidePanel(): React.ReactNode | undefined | null {
    const { isFullScreen } = this.state;

    if (isFullScreen) {
      return null;
    }

    return (
      <div className="discovery-content-page__side">
        <DiscoveryContentFilters
          onToggleFilters={this.handleToggleFilters}
          onResetFilters={this.handleResetFilters}
        />
      </div>
    );
  }

  renderExplanationHeader(): React.ReactNode {
    return (
      <div className="discovery-content-page__exp_header">
        <div className="discovery-content-page__exp_header-title">
          {__t(`${msgKey}.browser_title`)}
        </div>
        <div className="discovery-content-page__exp_header-text">
          {__t(`${msgKey}.header_explanation`)}
        </div>
      </div>
    );
  }

  renderMainPanel(): React.ReactNode {
    const { isFullScreen } = this.state;

    const mainClasses = classnames('discovery-content-page__main', {
      'discovery-content-page__main--is-fullscreen': isFullScreen
    });

    return (
      <div className={mainClasses}>
        {this.renderExplanationHeader()}
        {this.renderSearch()}
        {this.renderInteraction()}
        {this.renderResults()}
      </div>
    );
  }

  render(): React.ReactNode {
    const { org } = this.props;
    const { isFullScreen } = this.state;

    const containerClasses = classnames('discovery-content-page', {
      'discovery-content-page--is-fullscreen': isFullScreen
    });

    return (
      <Page
        documentTitle={`${__t(`${msgKey}.browser_title`)}`}
        subnavigation={getDiscoverySubnavigation(org.id)}
      >
        <div className={containerClasses}>
          {this.renderSidePanel()}
          {this.renderMainPanel()}
        </div>
      </Page>
    );
  }
}

const mapStateToProps = ({ organizations, discoveryContent, consentReducer }): StateProps => ({
  org: organizations.currentOrganization,
  results: discoveryContent.results,
  numContentCreators: discoveryContent.numContentCreators,
  searchTerms: discoveryContent.filters.searchTerms,
  searchTermsLogicalOperator: discoveryContent.filters.searchTermsLogicalOperator,
  screenState: discoveryContent.screenState,
  isLoading: discoveryContent.isLoading,
  userChannelConsents: consentReducer.consents
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchContent: (options?: DiscoveryFilterOptions): Promise<void> => dispatch(getContent(options)),
  dispatchUpdateFilters: (filters: Partial<DiscoveryContentFiltersType>): Promise<void> => dispatch(updateContentFilters(filters)),
  dispatchUpdateScreenState: (screenState: DiscoveryScreenState): Promise<void> => dispatch(updateScreenState(screenState)),

  dispatchResetContent: () => (
    dispatch(resetContent())
  ),

  dispatchResetFilters: (clearSearchTerms: boolean) => (
    dispatch(resetFilters(clearSearchTerms))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscoveryContentPage);
